import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe, Shield, TrendingUp, Camera, Settings, Check,
  ArrowRight, Phone, MapPin, MessageCircle,
  Menu, X, ExternalLink, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LanguageProvider, useLang, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, EMAIL } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// WhatsApp brand icon (lucide has no WhatsApp glyph)
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// Language toggle (EN / 繁中)
const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  const base = "px-3 py-1.5 text-sm font-semibold transition-colors";
  return (
    <div
      data-testid="language-toggle"
      className={`flex items-center rounded-full border border-slate-300 overflow-hidden bg-white/80 backdrop-blur-sm ${compact ? "" : ""}`}
    >
      <button
        data-testid="lang-en"
        onClick={() => setLang("en")}
        className={`${base} ${lang === "en" ? "bg-ohlala text-white" : "text-slate-600 hover:text-ohlala"}`}
      >
        EN
      </button>
      <button
        data-testid="lang-zh"
        onClick={() => setLang("zh")}
        className={`${base} ${lang === "zh" ? "bg-ohlala text-white" : "text-slate-600 hover:text-ohlala"}`}
      >
        繁中
      </button>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { t } = useLang();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: t.nav.about },
    { href: "#features", label: t.nav.features },
    { href: "#pricing", label: t.nav.pricing },
    { href: "#portfolio", label: t.nav.portfolio },
    { href: "#contact", label: t.nav.contact }
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo-link">
            <div className="relative">
              <div className="w-24 h-10 bg-ohlala transform -skew-x-6 flex items-center justify-center">
                <span className="text-white font-heading font-bold text-xl skew-x-6">ohlala</span>
              </div>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-600 hover:text-ohlala transition-colors font-medium"
              >
                {link.label}
              </a>
            ))}
            <LangToggle />
            <a href="#contact">
              <Button
                data-testid="nav-cta-button"
                className="bg-ohlala hover:bg-ohlala-dark text-white rounded-full px-6"
              >
                {t.nav.getStarted}
              </Button>
            </a>
          </div>

          {/* Mobile controls */}
          <div className="md:hidden flex items-center gap-3">
            <LangToggle compact />
            <button
              data-testid="mobile-menu-button"
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t py-4"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 px-4 text-slate-600 hover:bg-slate-50"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="px-4 pt-2">
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-ohlala hover:bg-ohlala-dark text-white rounded-full">
                  {t.nav.getStarted}
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLang();
  return (
    <section data-testid="hero-section" className="min-h-screen pt-20 flex items-center bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232B5A9E' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-left">
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-block px-4 py-2 bg-ohlala-light text-ohlala text-sm font-semibold rounded-full">
                {t.hero.badge}
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
            >
              {t.hero.titleStart}
              <span className="text-ohlala">{t.hero.titleHighlight}</span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-slate-600 mb-4 leading-relaxed">
              {t.hero.sub1}
            </motion.p>
            <motion.p variants={fadeInUp} className="text-base text-slate-500 mb-8">
              {t.hero.sub2}
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 mb-8">
              <a href="#contact">
                <Button
                  data-testid="hero-cta-primary"
                  className="bg-ohlala hover:bg-ohlala-dark text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  {t.hero.ctaPrimary} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="#portfolio">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="border-2 border-ohlala text-ohlala hover:bg-ohlala-light rounded-full px-8 py-6 text-lg font-medium transition-all"
                >
                  {t.hero.ctaSecondary}
                </Button>
              </a>
            </motion.div>

            <motion.div variants={fadeInUp} className="inline-flex items-center gap-3 bg-slate-50 rounded-2xl px-6 py-4">
              <span className="text-3xl font-heading font-bold text-ohlala">HKD 5,000</span>
              <span className="text-slate-600">{t.hero.priceLabel}</span>
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80"
                alt="Happy business owner"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ohlala/20 to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3"
            >
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t.hero.floatTitle}</p>
                <p className="text-sm text-slate-500">{t.hero.floatSub}</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLang();
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-ohlala-light text-ohlala text-sm font-semibold rounded-full mb-6">
            {t.about.badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-8">
            {t.about.title}
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-8 leading-relaxed">
            {t.about.p1}
          </motion.p>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-12 leading-relaxed">
            {t.about.p2}
          </motion.p>
          <motion.div variants={fadeInUp} className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
            {t.about.stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl md:text-4xl font-heading font-bold text-ohlala">{stat.number}</p>
                <p className="text-slate-600 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Features Section (What You Get)
const FeaturesSection = () => {
  const { t } = useLang();
  const icons = [Globe, Shield, TrendingUp, Camera, Settings, MessageCircle];

  return (
    <section id="features" data-testid="features-section" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-ohlala-light text-ohlala text-sm font-semibold rounded-full mb-6">
            {t.features.badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {t.features.title}
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-2xl font-heading font-bold text-ohlala">
            {t.features.price}
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.features.items.map((feature, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                data-testid={`feature-card-${index}`}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-ohlala-light rounded-xl flex items-center justify-center mb-6">
                  <Icon className="w-7 h-7 text-ohlala" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const { t } = useLang();
  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-ohlala-light text-ohlala text-sm font-semibold rounded-full mb-6">
            {t.pricing.badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
            {t.pricing.title}
          </motion.h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="grid md:grid-cols-2 gap-8">
          {/* Main Package */}
          <div data-testid="pricing-main-package" className="bg-white rounded-3xl p-8 md:p-10 border-2 border-ohlala shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-ohlala text-white text-sm font-semibold px-4 py-2 rounded-bl-xl">
              {t.pricing.mostPopular}
            </div>
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">{t.pricing.mainTitle}</h3>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-5xl font-heading font-bold text-ohlala">{t.pricing.mainPrice}</span>
              <span className="text-slate-500">{t.pricing.oneTime}</span>
            </div>
            <ul className="space-y-4 mb-8">
              {t.pricing.mainFeatures.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
            <a href="#contact">
              <Button data-testid="pricing-cta-main" className="w-full bg-ohlala hover:bg-ohlala-dark text-white rounded-full py-6 text-lg font-medium">
                {t.pricing.mainCta}
              </Button>
            </a>
          </div>

          {/* Yearly Renewal */}
          <div data-testid="pricing-yearly-package" className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-slate-200">
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">{t.pricing.yearTitle}</h3>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-5xl font-heading font-bold text-slate-700">{t.pricing.yearPrice}</span>
              <span className="text-slate-500">{t.pricing.perYear}</span>
            </div>
            <p className="text-slate-600 mb-6">{t.pricing.yearIntro}</p>
            <ul className="space-y-4 mb-8">
              {t.pricing.yearFeatures.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-ohlala-light rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-ohlala" />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-500 text-center">{t.pricing.yearNote}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Personal Service Section
const PersonalServiceSection = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-service-section" className="py-20 md:py-32 bg-ohlala-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-white text-ohlala text-sm font-semibold rounded-full mb-6">
              {t.personal.badge}
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {t.personal.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t.personal.p1}
            </motion.p>
            <motion.ul variants={stagger} className="space-y-4">
              {t.personal.items.map((item) => (
                <motion.li key={item} variants={fadeInUp} className="flex items-center gap-3">
                  <ChevronRight className="w-5 h-5 text-ohlala flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="relative">
            <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80" alt="Meeting with client" className="rounded-2xl shadow-xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Domain Section
const DomainSection = () => {
  const { t } = useLang();
  return (
    <section data-testid="domain-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-ohlala-light text-ohlala text-sm font-semibold rounded-full mb-6">
            {t.domain.badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t.domain.title}
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-12 max-w-2xl mx-auto">
            {t.domain.p1}
          </motion.p>
          <motion.div variants={fadeInUp} className="bg-slate-50 rounded-2xl p-8 max-w-lg mx-auto">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-4">{t.domain.exampleLabel}</p>
            <div className="space-y-3">
              {["yourbusinesshk.com", "yourbusinessbar.com", "yourbusinesshongkong.com"].map((domain) => (
                <div key={domain} className="bg-white rounded-lg px-4 py-3 border border-slate-200">
                  <code className="text-ohlala font-medium">{domain}</code>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const { t } = useLang();
  const portfolio = [
    { name: "The Brew Corner", url: "https://fotan-cafe.emergent.host" },
    { name: "The Hong Kong Ukulele Music Centre", url: "https://uke-music-hk.emergent.host" },
    { name: "King's Belly", url: "https://taipo-hideaway.emergent.host" },
    { name: "MISSO", url: "https://sleek-style-hk.emergent.host" },
    { name: "Dusk till Dawn", url: "https://dusk-till-dawn-1.emergent.host" },
    { name: "Asra", url: "https://asra-fotan.emergent.host" },
    { name: "Delaney's", url: "https://irish-pub-hk.emergent.host" },
    { name: "Ned Kelly's", url: "https://ned-kelly-site.emergent.host" }
  ];

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-ohlala-light text-ohlala text-sm font-semibold rounded-full mb-6">
            {t.portfolio.badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
            {t.portfolio.title}
          </motion.h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {portfolio.map((item, index) => (
            <motion.a
              key={item.name}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeInUp}
              data-testid={`portfolio-card-${index}`}
              className="group relative rounded-2xl overflow-hidden aspect-[16/10] block bg-slate-200 border border-slate-200 shadow-lg hover:shadow-xl transition-all"
            >
              <img
                src={`https://s.wordpress.com/mshots/v1/${encodeURIComponent(item.url)}?w=1280&h=800`}
                alt={`${item.name} website preview`}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-1">{item.name}</h3>
                    <p className="text-white/70">{t.portfolio.types[index]}</p>
                  </div>
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm group-hover:bg-ohlala transition-colors">
                    <ExternalLink className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">
                {t.portfolio.viewLive}
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const { t } = useLang();
  return (
    <section data-testid="how-it-works-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-ohlala-light text-ohlala text-sm font-semibold rounded-full mb-6">
            {t.howItWorks.badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
            {t.howItWorks.title}
          </motion.h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-4 gap-8">
          {t.howItWorks.steps.map((step, index) => (
            <motion.div key={index} variants={fadeInUp} data-testid={`step-${index + 1}`} className="text-center relative">
              <div className="w-16 h-16 bg-ohlala text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-heading font-bold">
                {index + 1}
              </div>
              {index < 3 && (
                <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-ohlala to-ohlala-light" />
              )}
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-slate-600">{step.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLang();
  const [formData, setFormData] = useState({
    name: "", business_name: "", phone: "", email: "", business_type: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message || t.contact.success);
      setFormData({ name: "", business_name: "", phone: "", email: "", business_type: "", message: "" });
    } catch (error) {
      toast.error(t.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-ohlala-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Info */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-white text-ohlala text-sm font-semibold rounded-full mb-6">
              {t.contact.badge}
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {t.contact.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-10">
              {t.contact.p1}
            </motion.p>

            <motion.div variants={stagger} className="space-y-6">
              <motion.div variants={fadeInUp} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <Phone className="w-5 h-5 text-ohlala" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500">{t.contact.phoneLabel}</p>
                  <p className="text-slate-900 font-medium">{PHONE_DISPLAY}</p>
                </div>
              </motion.div>

              {/* WhatsApp + Call buttons */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-button"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb959] text-white font-medium rounded-full px-5 py-3 shadow-sm transition-colors"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  {t.contact.whatsappCta}
                </a>
                <a
                  href={PHONE_TEL}
                  data-testid="call-button"
                  className="inline-flex items-center gap-2 bg-ohlala hover:bg-ohlala-dark text-white font-medium rounded-full px-5 py-3 shadow-sm transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {t.contact.callCta}
                </a>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <MapPin className="w-5 h-5 text-ohlala" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t.contact.areaLabel}</p>
                  <p className="text-slate-900 font-medium">{t.contact.area}</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right - Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.nameLabel}</label>
                  <Input data-testid="input-name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t.contact.namePh} className="rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.businessLabel}</label>
                  <Input data-testid="input-business-name" required value={formData.business_name} onChange={(e) => setFormData({ ...formData, business_name: e.target.value })} placeholder={t.contact.businessPh} className="rounded-lg" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.phoneFieldLabel}</label>
                  <Input data-testid="input-phone" required type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={t.contact.phonePh} className="rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.emailLabel}</label>
                  <Input data-testid="input-email" required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t.contact.emailPh} className="rounded-lg" />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.typeLabel}</label>
                <Select value={formData.business_type} onValueChange={(value) => setFormData({ ...formData, business_type: value })}>
                  <SelectTrigger data-testid="select-business-type" className="rounded-lg">
                    <SelectValue placeholder={t.contact.typePh} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bar">{t.contact.typeOptions.bar}</SelectItem>
                    <SelectItem value="restaurant">{t.contact.typeOptions.restaurant}</SelectItem>
                    <SelectItem value="pub">{t.contact.typeOptions.pub}</SelectItem>
                    <SelectItem value="cafe">{t.contact.typeOptions.cafe}</SelectItem>
                    <SelectItem value="other">{t.contact.typeOptions.other}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">{t.contact.messageLabel}</label>
                <Textarea data-testid="input-message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder={t.contact.messagePh} className="rounded-lg min-h-[120px]" />
              </div>

              <Button type="submit" data-testid="contact-submit-button" disabled={isSubmitting} className="w-full bg-ohlala hover:bg-ohlala-dark text-white rounded-full py-6 text-lg font-medium">
                {isSubmitting ? t.contact.submitting : t.contact.submit}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLang();
  const quickLinks = [
    { href: "#about", label: t.nav.about },
    { href: "#features", label: t.nav.features },
    { href: "#pricing", label: t.nav.pricing },
    { href: "#portfolio", label: t.nav.portfolio },
    { href: "#contact", label: t.nav.contact }
  ];

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <div className="w-28 h-12 bg-ohlala transform -skew-x-6 flex items-center justify-center mb-4">
              <span className="text-white font-heading font-bold text-2xl skew-x-6">ohlala</span>
            </div>
            <p className="text-slate-400 mb-4">{t.footer.tagline}</p>
            <p className="text-slate-500 text-sm">
              {t.footer.desc1}
              <br />
              {t.footer.desc2}
            </p>
          </div>

          <div>
            <h4 className="font-heading font-semibold mb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-slate-400 hover:text-white transition-colors">{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold mb-4">{t.footer.contactTitle}</h4>
            <ul className="space-y-3 text-slate-400">
              <li className="flex items-center gap-3">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1fb959] flex items-center justify-center transition-colors" aria-label="WhatsApp">
                  <WhatsAppIcon className="w-4 h-4 text-white" />
                </a>
                <a href={PHONE_TEL} data-testid="footer-call" className="w-9 h-9 rounded-full bg-ohlala hover:bg-ohlala-dark flex items-center justify-center transition-colors" aria-label="Call">
                  <Phone className="w-4 h-4 text-white" />
                </a>
                <a href={PHONE_TEL} className="hover:text-white transition-colors">{PHONE_DISPLAY}</a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors">{EMAIL}</a>
              </li>
              <li>{t.footer.location}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Ohlala. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <LanguageProvider>
      <div className="min-h-screen">
        <Navigation />
        <HeroSection />
        <AboutSection />
        <FeaturesSection />
        <PricingSection />
        <PersonalServiceSection />
        <DomainSection />
        <PortfolioSection />
        <HowItWorksSection />
        <ContactSection />
        <Footer />
      </div>
    </LanguageProvider>
  );
};

export default LandingPage;
